<template>
  <section class="my-details">
    <account-section
      :email="email"
      :logged-in-user-email="loggedInUserEmail"
      :logged-in="loggedIn"
      @login="$emit('login')"
      @logout="$emit('logout')"
    />
    <translate v-if="!alreadyHasAnAccount" tag="div" class="help-text">
      You'll have the option to create an account and password below
    </translate>
    <base-input
      v-model="email"
      autocapitalize="off"
      autocomplete="email"
      autocorrect="off"
      name="email"
      spellcheck="false"
      type="email"
      :required="true"
      class="email-address"
      :validation-rules="emailValidationSchema"
      data-test="email-address-input"
      @field-blur="$emit('email-blur')"
    >
      <template #label>
        {{ $t('Email address') }}
      </template>
      <template #helpText>
        <transition name="fade">
          <span v-if="alreadyHasAnAccount && !loggedIn">
            {{ $t('You already have an account.') }}
            <login-modal modal-hash="login-or-reset-form" :email="email">
              <translate
                tag="a"
                data-test="login-link-account-exists"
                @click.prevent="$emit('login', email)"
              >
                Log in or reset your password
              </translate>
            </login-modal>
          </span>
        </transition>
        {{ $t('We will send your confirmation email to this address') }}
      </template>
      <template #errorMessage="{ failedRules }">
        <translate v-if="failedRules.includes('required')" tag="div">
          Please enter your email address
        </translate>
        <translate v-if="failedRules.includes('email')" tag="div">
          Please enter a valid email address
        </translate>
      </template>
    </base-input>

    <base-input
      v-model="firstName"
      type="text"
      autocomplete="given-name"
      autocorrect="off"
      maxlength="100"
      name="first_name"
      :required="true"
      :validation-rules="firstNameValidationSchema"
    >
      <template #label>
        {{ $t('First name') }}
      </template>
      <template #errorMessage>
        {{ $t('Please tell us your first name') }}
      </template>
    </base-input>

    <base-input
      v-model="lastName"
      type="text"
      autocomplete="family-name"
      autocorrect="off"
      maxlength="100"
      name="last_name"
      :required="true"
      :validation-rules="lastNameValidationSchema"
    >
      <template #label>
        {{ $t('Last name') }}
      </template>
      <template #errorMessage>
        {{ $t('Please tell us your last name') }}
      </template>
    </base-input>

    <base-country
      v-model="addressCountry"
      autocomplete="country"
      name="country"
      type="select"
      :validation-rules="countryValidationSchema"
      :required="true"
    >
      <template #label>
        {{ $t('Country of residence') }}
      </template>
      <template #errorMessage>
        {{ $t('Please choose your country of residence') }}
      </template>
    </base-country>

    <base-phone
      :model-value="phone"
      name="telephone"
      type="tel"
      :required="true"
      @update:model-value="updatePhoneField"
    >
      <template #label>
        {{ $t('Mobile phone number') }}
      </template>
      <template #labelHelp> ({{ $t('landline if no mobile') }}) </template>
      <template #helpText>
        {{ $t('In case {campsiteName} needs to call you', { campsiteName }) }}
      </template>
    </base-phone>

    <base-checkbox v-model="sendTextMessage" name="text_message">
      {{ $t('Text me a booking summary (free of charge)') }}
    </base-checkbox>

    <base-input
      v-model="addressStreet"
      autocomplete="street-address"
      autocorrect="off"
      maxlength="256"
      name="address"
      :required="true"
      :validation-rules="addressValidationSchema"
    >
      <template #label>
        {{ $t('Address') }}
      </template>
      <template #errorMessage>
        {{ $t('Please tell us your address') }}
      </template>
    </base-input>

    <base-input
      v-model="addressCity"
      name="city"
      autocomplete="address-level2"
      maxlength="256"
      autocorrect="off"
      :required="true"
      :validation-rules="cityValidationSchema"
    >
      <template #label>
        {{ $t('Town / City') }}
      </template>
      <template #errorMessage>
        {{ $t('Please tell us your town / city') }}
      </template>
    </base-input>

    <base-input
      v-if="shouldShowPostCode"
      v-model="addressPostCode"
      autocapitalize="characters"
      autocomplete="postal-code"
      autocorrect="off"
      maxlength="10"
      name="postcode"
      required="isPostCodeRequired"
      :validation-rules="postcodeValidationSchema"
      class="postcode-section"
    >
      <template #label>
        {{ $t('Postcode') }}
      </template>
      <template #errorMessage>
        {{ $t('Please tell us your postcode') }}
      </template>
    </base-input>

    <password-section
      v-if="!loggedIn"
      v-model="password"
      name="password"
      type="password"
      :already-has-an-account="alreadyHasAnAccount"
      :email="email"
      @login="$emit('login')"
    />
  </section>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { InternationalPhone } from '~/components/ui/BasePhone.vue'
import { type ContactDetails } from '../submit/schema'
import * as z from 'zod'

export const emptyInternationalPhone: InternationalPhone = {
  countryDialingCode: '',
  number: '',
}

@Component({
  emits: ['update:modelValue', 'login', 'logout'],
})
class BookingMyDetails extends Vue {
  @Prop()
  stateToStrings: string[]

  @Prop()
  loggedIn: boolean

  @Prop()
  contactDetails: ContactDetails

  @Prop()
  campsiteName: string

  @Prop()
  loggedInUserEmail: string

  get postcodeValidationSchema() {
    if (this.isPostCodeRequired) {
      return z
        .string({ required_error: 'required' })
        .min(1, { message: 'required' })
    }
    return undefined
  }

  emailValidationSchema = z
    .string({ required_error: 'required' })
    .nonempty({ message: 'required' })
    .email({ message: 'email' })

  firstNameValidationSchema = z
    .string({ required_error: 'required' })
    .nonempty({ message: 'required' })

  lastNameValidationSchema = z
    .string({ required_error: 'required' })
    .nonempty({ message: 'required' })

  addressValidationSchema = z
    .string({ required_error: 'required' })
    .nonempty({ message: 'required' })

  cityValidationSchema = z
    .string({ required_error: 'required' })
    .nonempty({ message: 'required' })

  countryValidationSchema = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })

  get email(): string {
    return this.contactDetails.email
  }

  set email(email: string) {
    this.input({ email })
  }

  get firstName(): string {
    return this.contactDetails.firstName
  }

  set firstName(firstName: string) {
    this.input({ firstName })
  }

  get lastName(): string {
    return this.contactDetails.lastName
  }

  set lastName(lastName: string) {
    this.input({ lastName })
  }

  get phone(): InternationalPhone {
    return this.contactDetails.phone || emptyInternationalPhone
  }

  updatePhoneField(value) {
    this.input({ phone: { ...value } })
  }

  get password(): string | undefined {
    return this.contactDetails.password
  }

  set password(password: string | undefined) {
    this.input({ password })
  }

  get addressCity(): string {
    return this.contactDetails.address?.city || ''
  }

  set addressCity(addressCity: string) {
    this.input({
      address: { ...this.contactDetails.address, city: addressCity },
    })
  }

  get addressStreet(): string {
    return this.contactDetails.address?.street || ''
  }

  set addressStreet(addressStreet: string) {
    this.input({
      address: { ...this.contactDetails.address, street: addressStreet },
    })
  }

  get addressPostCode(): string {
    return this.contactDetails.address?.postCode || ''
  }

  set addressPostCode(addressPostCode: string) {
    this.input({
      address: { ...this.contactDetails.address, postCode: addressPostCode },
    })
  }

  get addressCountry(): string {
    return this.contactDetails.address?.country || ''
  }

  set addressCountry(addressCountry: string) {
    this.input({
      address: { ...this.contactDetails.address, country: addressCountry },
      phone: {
        ...(this.contactDetails.phone || emptyInternationalPhone),
        countryDialingCode: addressCountry,
      },
    })
  }

  get sendTextMessage(): boolean {
    return this.contactDetails.sendTextMessage
  }

  set sendTextMessage(sendTextMessage: boolean) {
    this.input({ sendTextMessage })
  }

  input(contactDetails: Partial<ContactDetails>): ContactDetails {
    const input: ContactDetails = Object.assign(
      {},
      this.contactDetails,
      contactDetails,
    )
    this.$emit('update:modelValue', input)
    return input
  }

  get alreadyHasAnAccount() {
    return (
      this.stateToStrings.includes('dataEntry.data.knownEmail') || this.loggedIn
    )
  }

  get shouldShowPostCode() {
    return this.addressCountry && this.isPostCodeRequired
  }

  @Watch('shouldShowPostCode', { immediate: false })
  setNoPostcode() {
    this.input({
      address: {
        ...this.contactDetails.address,
        noPostCode: !this.shouldShowPostCode ? 'true' : undefined,
      },
    })
  }

  get isPostCodeRequired() {
    return useCountriesStore().countries.find(
      (e) => e.alpha2 === this.addressCountry,
    )?.hasPostCode
  }
}

export default toNative(BookingMyDetails)
</script>

<style scoped>
.my-details > div {
  margin-bottom: 0.8em;
  display: block;
}

.my-details :deep(.countries) {
  margin-bottom: 1.2em;
}

.my-details > .help-text {
  margin-top: 0.5em;
}

.email-address {
  margin: 0.75em 0 0;
}

.my-details :deep(.reset-password .modal-container) {
  width: 94vw;
}

.postcode-wrapper :deep(.input-error-msg) {
  max-width: 10em;
}
</style>
